import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import SharedHeader from './SharedHeader';
import { getClaimRequests, updateClaimRequest, createCustomerTransaction } from '../../services/api';
import { Search, Filter, ArrowUpDown, Calendar, ArrowLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';

// Local compute: 0.1 pts per ₹ (₹100 => 10 pts)
const computePointsForAmount = (amt) => {
  const perRupee = 0.1;
  const n = Number(amt) || 0;
  return Math.max(0, Math.floor(n * perRupee));
};

const CreditByMobilePage = ({ onBack }) => {
  const { currentUser } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Data
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters (mobile + date only)
  const [searchMobile, setSearchMobile] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // all | today | last7 | last30 | thisMonth | custom
  const [customRange, setCustomRange] = useState({ start: '', end: '' });

  // Sorting
  const [sortField, setSortField] = useState('date'); // mobile | amount | date | points | status
  const [sortDirection, setSortDirection] = useState('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch claims for this business
  const fetchClaims = async () => {
    try {
      const myEmail = (currentUser?.email || '').toLowerCase();
      if (!myEmail) return;
      setLoading(true);
  
      const resp = await getClaimRequests({});
      const list = Array.isArray(resp.extractedData) 
        ? resp.extractedData 
        : (resp.extractedData?.records || []);
  
      const mine = (list || [])
        // Only include items that belong to current user AND have customerMobile
        .filter(c => 
          (String(c.businessEmail || c.email || '').toLowerCase() === myEmail) &&
          c.customerMobile // only objects with customerMobile
        )
        .map((it, idx) => ({
          id: it._id || it.id || String(idx),
          customerMobile: it.customerMobile,
          amount: Number(it.amount || 0),
          status: (it.status || 'pending').toLowerCase(),
          requestDate: it.requestDate || it.created_on || new Date().toISOString(),
        }));
  console.log("ssssssss",list)
      setClaims(mine);
    } catch (e) {
      console.warn('Failed to load claims', e?.message || e);
      toast.error('Failed to load incoming purchases');
    } finally {
      setLoading(false);
    }
  };
  




  useEffect(() => {
    fetchClaims();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.email]);

  // Poll every 15s to stay fresh
  useEffect(() => {
    let id;
    (async () => {
      if (!currentUser?.email) return;
      id = setInterval(fetchClaims, 15000);
    })();
    return () => { if (id) clearInterval(id); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.email]);

  const applyDateFilter = (items) => {
    if (!Array.isArray(items)) return [];
    const now = new Date();

    const inRange = (d) => {
      if (dateFilter === 'all') return true;
      const date = new Date(d);
      if (dateFilter === 'today') return date.toDateString() === now.toDateString();
      if (dateFilter === 'last7') {
        const seven = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        return date >= seven && date <= now;
      }
      if (dateFilter === 'last30') {
        const thirty = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
        return date >= thirty && date <= now;
      }
      if (dateFilter === 'thisMonth') {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        return date >= start && date <= now;
      }
      if (dateFilter === 'custom' && customRange.start && customRange.end) {
        const start = new Date(customRange.start);
        const end = new Date(customRange.end);
        return date >= start && date <= end;
      }
      return true;
    };

    return items.filter(x => inRange(x.requestDate));
  };

  const filteredSorted = useMemo(() => {
    let rows = [...claims];
    // Mobile filter (customerMobile only)
    if (searchMobile.trim()) {
      const q = searchMobile.trim();
      rows = rows.filter(r => String(r.customerMobile || '').includes(q));
    }
    // Date filter
    rows = applyDateFilter(rows);

    // Sorting
    rows.sort((a, b) => {
      let av, bv;
      switch (sortField) {
        case 'mobile':
          av = String(a.customerMobile || '');
          bv = String(b.customerMobile || '');
          break;
        case 'amount':
          av = Number(a.amount) || 0;
          bv = Number(b.amount) || 0;
          break;
        case 'points':
          av = computePointsForAmount(a.amount || 0);
          bv = computePointsForAmount(b.amount || 0);
          break;
        case 'status':
          av = (a.status || '').toLowerCase();
          bv = (b.status || '').toLowerCase();
          break;
        case 'date':
        default:
          av = new Date(a.requestDate).getTime();
          bv = new Date(b.requestDate).getTime();
      }
      const cmp = av > bv ? 1 : (av < bv ? -1 : 0);
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return rows;
  }, [claims, searchMobile, dateFilter, customRange, sortField, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / itemsPerPage));
  const pageRows = filteredSorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const toggleSort = (field) => {
    if (sortField === field) setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDirection('asc'); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SharedHeader
        title="Credit By Mobile"
        subtitle="Incoming purchases (auto-credited)"
        onMenuAction={(action) => {
          if (action === 'dashboard') navigate('/business');
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => (onBack ? onBack() : navigate(-1))}
            className="px-3 py-2 rounded-lg border hover:bg-gray-50 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                value={searchMobile}
                onChange={(e) => { setSearchMobile(e.target.value.replace(/[^0-9+]/g, '')); setCurrentPage(1); }}
                placeholder="Filter by customer mobile"
                className="pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 w-56"
              />
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <select
                value={dateFilter}
                onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 border rounded-lg"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="last7">Last 7 Days</option>
                <option value="last30">Last 30 Days</option>
                <option value="thisMonth">This Month</option>
                <option value="custom">Custom</option>
              </select>
              {dateFilter === 'custom' && (
                <>
                  <input type="date" value={customRange.start} onChange={(e)=>setCustomRange(p=>({...p,start:e.target.value}))} className="px-2 py-2 border rounded-lg" />
                  <input type="date" value={customRange.end} onChange={(e)=>setCustomRange(p=>({...p,end:e.target.value}))} className="px-2 py-2 border rounded-lg" />
                </>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer" onClick={()=>toggleSort('mobile')}>
                    Customer Mobile <ArrowUpDown className="inline w-3 h-3 ml-1 text-gray-400" />
                  </th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700 cursor-pointer" onClick={()=>toggleSort('amount')}>
                    Amount (₹) <ArrowUpDown className="inline w-3 h-3 ml-1 text-gray-400" />
                  </th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700 cursor-pointer" onClick={()=>toggleSort('points')}>
                    Points <ArrowUpDown className="inline w-3 h-3 ml-1 text-gray-400" />
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer" onClick={()=>toggleSort('date')}>
                    Date <ArrowUpDown className="inline w-3 h-3 ml-1 text-gray-400" />
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer" onClick={()=>toggleSort('status')}>
                    Status <ArrowUpDown className="inline w-3 h-3 ml-1 text-gray-400" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="py-10 text-center text-gray-500">Loading...</td></tr>
                ) : pageRows.length ? (
                  pageRows.map(row => (
                    <tr key={row.id} className="border-t hover:bg-gray-50">
                      <td className="py-3 px-4 text-blue-600 font-semibold">{row.customerMobile || '—'}</td>
                      <td className="py-3 px-4 text-right text-gray-800">₹{Number(row.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="py-3 px-4 text-right text-blue-600 font-semibold">{computePointsForAmount(row.amount)}</td>
                      <td className="py-3 px-4 text-gray-600">{new Date(row.requestDate).toLocaleDateString()}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.status === 'approved' ? 'bg-green-100 text-green-700' : row.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`}>
                          {row.status.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={7} className="py-10 text-center text-gray-500">No purchases found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between border-t p-4">
            <p className="text-gray-600 text-sm">Page {currentPage} of {totalPages} • {filteredSorted.length} items</p>
            <div className="flex items-center gap-2">
              <button onClick={()=>setCurrentPage(p=>Math.max(1, p-1))} disabled={currentPage===1} className="px-3 py-2 border rounded-lg disabled:opacity-50">Previous</button>
              {Array.from({length: totalPages}).slice(0,5).map((_,i)=>{
                const page=i+1;
                return (
                  <button key={page} onClick={()=>setCurrentPage(page)} className={`px-3 py-2 rounded-lg ${currentPage===page? 'bg-blue-600 text-white' : 'border'}`}>{page}</button>
                );
              })}
              <button onClick={()=>setCurrentPage(p=>Math.min(totalPages, p+1))} disabled={currentPage===totalPages} className="px-3 py-2 border rounded-lg disabled:opacity-50">Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditByMobilePage;
